import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Chip from '@mui/material/Chip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoNotificationsOutline } from 'react-icons/io5';

export default function AdminNotifications() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  const load = () => {
    const q = filter === 'unread' ? '?unread=true' : '';
    api(`/api/admin/notifications${q}`)
      .then((data) => { setList(data); setError(''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), [filter]);

  const handleMarkRead = async (id) => {
    try {
      await api(`/api/admin/notifications/${id}/read`, { method: 'PATCH' });
      setList((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    } catch (e) {
      setError(e.message);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const now = new Date();
    const diff = now - dt;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return dt.toLocaleDateString();
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Notifications"
        subheader="Rental and order notifications"
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip label="All" size="small" color={filter === 'all' ? 'primary' : 'default'} onClick={() => setFilter('all')} />
            <Chip label="Unread" size="small" color={filter === 'unread' ? 'primary' : 'default'} onClick={() => setFilter('unread')} />
          </Box>
        }
      />
      <CardContent>
        {error && <Typography color="error" className="admin-error" sx={{ mb: 2 }}>{error}</Typography>}
        {list.length === 0 ? (
          <AdminEmptyState message="No notifications." icon={IoNotificationsOutline} />
        ) : (
          <List dense>
            {list.map((n) => (
              <ListItem
                key={n.id}
                sx={{
                  bgcolor: n.read_at ? 'transparent' : 'action.hover',
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      {n.body && <Typography variant="body2" color="text.secondary">{n.body}</Typography>}
                      <Typography variant="caption" color="text.secondary">{formatDate(n.created_at)} · {n.type}</Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  {!n.read_at && (
                    <Button size="small" onClick={() => handleMarkRead(n.id)}>Mark read</Button>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
